import React, {
  ForwardRefRenderFunction,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";

import { Grid, MenuItem } from "@mui/material";
import { colors } from "@zapalacorp/tokens";
import { PublishTaskIcon } from "../../../modules/moving/components/Diary/views/DiaryResourcesView/subcomponent/Viewers/ResourceTeamViewer/TeamCard/styled";
import JoditEditor, { IJoditEditorProps } from "jodit-react";
import { useTranslation } from "react-i18next";
import isEmail from "validator/lib/isEmail";
import * as yup from "yup";

import { useAppState } from "../../../context/StateContext";
import useYupValidation from "../../../hooks/useYupValidation";
import { Customer } from "../../../models/customer";
import { Sales } from "../../../models/sales";
import { EmailTemplate, EmailTemplateToSend } from "../../../models/templates/email.template";
import { EmailTemplates, EmailtTemplateTagsEnum, ShareNotificationId } from "../../../util/enums";
import { Input, Select } from "../../Form";
import { FormError } from "../../Form/FormError";
import Modal from "../Modal";
import SkeletonComponent from "../Skeleton";
import { NotificationToast } from "../Toast/NotificationToast";
import { useRequests } from "./hooks/useRequests";
import useStyles, {
  GridContainer,
  GridContainerTitle,
  Label,
  ModalBody,
  Required,
  TextEditorContainer,
  TextEditorGridContainer,
} from "./styled";

export interface SendEmailModalHandle {
  setOpenModal: (title: string, customer?: Customer) => void;
  onCloseModal: () => void;
}

interface SendEmailModalProps {
  shipmentId?: number;
  usedIn?: string;
  quote: Sales;
}

const SendEmailModal: ForwardRefRenderFunction<SendEmailModalHandle, SendEmailModalProps> = (
  props,
  ref
) => {
  const { t: translate } = useTranslation();
  const textEditorRef = useRef(null);
  const { getAllEmailTemplates, sendEmailTo, useProcessEmailTemplateRequest } = useRequests();
  const { currentUser } = useAppState();
  const classes = useStyles();

  const [open, setOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState<string>("");
  const [showBcc, setShowBcc] = useState(false);
  const [showCc, setShowCc] = useState(false);
  const [selectedEmailTemplate, setSelectedEmailTemplate] = useState<EmailTemplate>(
    {} as EmailTemplate
  );
  const [sendEmailTemplate, setSendEmailTemplate] = useState<EmailTemplateToSend>(
    {} as EmailTemplateToSend
  );
  const [errors, setErrors] = useState<yup.ValidationError[]>([]);

  const { fieldHasError, getErrorByField } = useYupValidation(errors);

  const { usedIn, shipmentId, quote } = props;

  const schema = yup.object().shape({
    selectedEmailTemplateId: yup.number().typeError(translate("thisFieldIsRequired")),
    from: yup
      .string()
      .required(translate("thisFieldIsRequired"))
      .typeError(translate("thisFieldIsRequired")),
    subject: yup
      .string()
      .required(translate("thisFieldIsRequired"))
      .typeError(translate("thisFieldIsRequired")),
    html: yup
      .string()
      .required(translate("thisFieldIsRequired"))
      .typeError(translate("thisFieldIsRequired")),
    to: yup
      .string()
      .trim()
      .required(translate("thisFieldIsRequired"))
      .typeError(translate("thisFieldIsRequired"))
      .test("email-check", translate("thisFieldIsRequired"), (email) =>
        email ? isEmail(email) : false
      ),
  });

  const onValidation = (email: EmailTemplateToSend) => {
    try {
      schema.validateSync(
        {
          selectedEmailTemplateId: selectedEmailTemplate?.id,
          from: email.from,
          subject: email.subject,
          html: email.html,
          to: email.to,
        },
        { abortEarly: false }
      );

      setErrors([]);

      return true;
    } catch (err) {
      if (err instanceof yup.ValidationError) setErrors(err?.inner);

      return false;
    }
  };

  const { data: emailTemplates, isLoading: isEmailTemplatesLoading } = getAllEmailTemplates(usedIn);
  const { mutate, isLoading, isSuccess } = sendEmailTo();
  const { mutate: processEmailTemplate, isLoading: isProcessEmailTemplateLoading } =
    useProcessEmailTemplateRequest({
      onSuccess({ html }) {
        setSelectedEmailTemplate((prev) => {
          return { ...prev, html: html };
        });
      },
    });

  const config: IJoditEditorProps["config"] = {
    placeholder: translate("bodyOfEmail"),
    buttons: [
      "bold",
      "italic",
      "underline",
      "indent",
      "outdent",
      "left",
      "link",
      "image",
      "undo",
      "redo",
    ],
    toolbarAdaptive: false,
    removeButtons: ["fullsize"],
    uploader: {
      insertImageAsBase64URI: true,
    },
  } as IJoditEditorProps["config"];

  const setOpenModal = (title: string, customer?: Customer) => {
    setOpen(true);
    setModalTitle(title);
    setSendEmailTemplate({ to: customer?.email } as EmailTemplateToSend);
  };

  const onSetAlignment = (hasError?: boolean) => (hasError ? "baseline" : "center");

  const onClearFields = () => {
    setSelectedEmailTemplate({} as EmailTemplate);
    setSendEmailTemplate({} as EmailTemplateToSend);
    setModalTitle("");
    setShowBcc(false);
    setShowCc(false);
  };

  const onSubmit = () => {
    let sendEmail: EmailTemplateToSend;

    if (selectedEmailTemplate) {
      sendEmail = {
        ...sendEmailTemplate,
        subject: selectedEmailTemplate.subject,
        from: currentUser?.company?.name,
        html: selectedEmailTemplate?.html,
        name: selectedEmailTemplate?.name,
      } as EmailTemplateToSend;
    } else {
      sendEmail = {
        ...sendEmailTemplate,
        from: currentUser?.company?.name,
      } as EmailTemplateToSend;
    }

    if (shipmentId) {
      sendEmail = {
        ...sendEmail,
        shipmentId,
      } as EmailTemplateToSend;
    }

    if (onValidation(sendEmail)) {
      mutate(sendEmail);
    }
  };

  const onCloseModal = () => {
    onClearFields();
    setOpen(false);
  };

  useEffect(() => {
    if (isSuccess) {
      NotificationToast.success(translate("communicationSentSuccessfully"), {
        id: ShareNotificationId.SEND_SHIPMENT_EMAIL,
      });
      onCloseModal();
    }
  }, [isSuccess]);

  useImperativeHandle(ref, () => ({
    setOpenModal,
    onCloseModal,
  }));

  useEffect(() => {
    if (selectedEmailTemplate.html !== undefined) {
      processEmailTemplate({
        data: {
          companyName: currentUser?.company?.name,
          customerFullName: quote?.customer?.fullName,
          quoteReference: quote?.reference,
          userFullname: quote?.user?.fullName,
        },
        html: selectedEmailTemplate.html,
      });
    }
  }, [selectedEmailTemplate.id]);

  // This filter is an paleative solution for the send estimate link removal
  const filteredTemplates = emailTemplates?.data?.filter(
    (template) => !template.name.includes(EmailTemplates.SEND_ESTIMATE)
  );

  const sendEmailBtn = () => {
    return (
      <div
        style={{
          display: "flex",
          gap: "8px",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <PublishTaskIcon color={colors.white} size={18} />
        <div>{translate("send")}</div>
      </div>
    );
  };

  return (
    <Modal
      open={open}
      handleClose={onCloseModal}
      title={modalTitle}
      disableEnforceFocus
      data-cy="sendEmailModal"
      dataCyTitle="sendEmailModalTitle"
      showActionButtons
      actionButtonProps={{
        dataCyCancel: "cancelButton",
        dataCySave: "submitButton",
        textSave: sendEmailBtn(),
        loading: isLoading,
        saveDisabled: isLoading,
        cancelDisabled: isLoading,
        onCancel: onCloseModal,
        onSave: onSubmit,
      }}
      className={classes.modalBody}
    >
      <ModalBody>
        <Grid
          container
          direction="row"
          alignItems="flex-start"
          spacing={2}
          style={{
            marginBottom: "24px",
          }}
        >
          <GridContainer item xs={12} lg={12}>
            <GridContainerTitle>{translate("emailDetails")}</GridContainerTitle>
          </GridContainer>
        </Grid>
        <Grid
          item
          container
          direction="row"
          xs={12}
          md={12}
          spacing={1}
          style={{
            marginBottom: "10px",
          }}
        >
          <Grid
            item
            container
            direction="row"
            alignItems={onSetAlignment(fieldHasError("selectedEmailTemplateId"))}
            xs={6}
            md={6}
            spacing={1}
          >
            <Grid
              item
              xs={12}
              md={2}
              style={{
                marginRight: "10px",
              }}
            >
              <Label>{translate("template")}</Label>
            </Grid>
            <Grid item xs={12} md={8}>
              <SkeletonComponent isLoading={isEmailTemplatesLoading} isInput>
                <Select
                  name="template"
                  value={selectedEmailTemplate?.id}
                  data-cy="selectEmailTemplate"
                  onChange={(event) => {
                    if (Number(event.target.value)) {
                      const emailTemplate = emailTemplates?.data.find(
                        (email) => email.id === Number(event.target.value)
                      );

                      if (emailTemplate) {
                        setSelectedEmailTemplate((state) => ({
                          ...(state || {}),
                          ...emailTemplate,
                        }));
                      }
                    } else {
                      setSelectedEmailTemplate({} as EmailTemplate);
                    }
                  }}
                  error={fieldHasError("selectedEmailTemplateId")}
                  errorMessage={getErrorByField("selectedEmailTemplateId")}
                >
                  {filteredTemplates?.map((emailTemplate) => (
                    <MenuItem key={emailTemplate.name} value={emailTemplate.id}>
                      {emailTemplate.name}
                    </MenuItem>
                  ))}
                </Select>
              </SkeletonComponent>
            </Grid>
          </Grid>
          <Grid item container direction="row" xs={12} md={6} spacing={1}>
            <Grid item container direction="row" alignItems="flex-start" xs={12} md={6}>
              <Grid item xs={12} md={6}>
                <Label withOnClick onClick={() => setShowCc((state) => !state)}>
                  {showCc ? translate("hideCc") : translate("showCc")}
                </Label>
              </Grid>
              <Grid item xs={12} md={6}>
                <Label withOnClick onClick={() => setShowBcc((state) => !state)}>
                  {showBcc ? translate("hideBcc") : translate("showBcc")}
                </Label>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        <Grid
          item
          container
          direction="row"
          xs={12}
          md={12}
          spacing={1}
          style={{
            marginBottom: "10px",
          }}
        >
          {showCc && (
            <Grid item container direction="row" alignItems="center" xs={12} md={6} spacing={1}>
              <Grid
                item
                xs={12}
                md={2}
                style={{
                  marginRight: "10px",
                }}
              >
                <Label>{translate("cc")}</Label>
              </Grid>
              <Grid item xs={12} md={8}>
                <Input
                  name="cc"
                  value={sendEmailTemplate?.cc}
                  onChange={(event) =>
                    setSendEmailTemplate((state) => ({ ...(state || {}), cc: event.target.value }))
                  }
                />
              </Grid>
            </Grid>
          )}
          {showBcc && (
            <Grid item container direction="row" alignItems="center" xs={12} md={6} spacing={1}>
              <Grid
                item
                xs={12}
                md={2}
                style={{
                  marginRight: "10px",
                }}
              >
                <Label>{translate("bcc")}</Label>
              </Grid>
              <Grid item xs={12} md={8}>
                <Input
                  name="bcc"
                  value={sendEmailTemplate?.bcc}
                  onChange={(event) =>
                    setSendEmailTemplate((state) => ({ ...(state || {}), bcc: event.target.value }))
                  }
                />
              </Grid>
            </Grid>
          )}
        </Grid>
        <Grid
          item
          container
          direction="row"
          xs={12}
          md={12}
          spacing={1}
          style={{
            marginBottom: "10px",
          }}
        >
          <Grid
            item
            container
            direction="row"
            alignItems={onSetAlignment(fieldHasError("from"))}
            xs={12}
            md={6}
            spacing={1}
          >
            <Grid
              item
              xs={12}
              md={2}
              style={{
                marginRight: "10px",
              }}
            >
              <Label>
                {translate("from")}
                <Required>*</Required>
              </Label>
            </Grid>
            <Grid item xs={12} md={8}>
              <Input name="from" value={currentUser?.company?.name ?? ""} disabled />
            </Grid>
          </Grid>
          {/* <Grid
            item
            container
            direction="row"
            alignItems={onSetAlignment(fieldHasError("from"))}
            xs={12}
            md={6}
            spacing={1}
          >
            <Grid
              item
              xs={12}
              md={2}
              style={{
                marginRight: "10px",
              }}
            >
              <Label>
                {translate("from")}
                <Required>*</Required>
              </Label>
            </Grid>
            <Grid item xs={12} md={8}>
              <Input name="from" value={currentUser?.company?.name ?? ""} disabled />
            </Grid>
          </Grid> */}
          <Grid
            item
            container
            direction="row"
            alignItems={onSetAlignment(fieldHasError("to"))}
            xs={12}
            md={6}
            spacing={1}
          >
            <Grid
              item
              xs={12}
              md={2}
              style={{
                marginRight: "10px",
              }}
            >
              <Label>
                {translate("to")}
                <Required>*</Required>
              </Label>
            </Grid>
            <Grid item xs={12} md={8}>
              <Input
                name="to"
                value={sendEmailTemplate?.to}
                onChange={(event) =>
                  setSendEmailTemplate((state) => ({
                    ...(state || {}),
                    to: event.target.value,
                  }))
                }
                error={fieldHasError("to")}
                errorMessage={getErrorByField("to")}
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid
          item
          container
          direction="row"
          xs={12}
          md={12}
          spacing={1}
          style={{
            marginBottom: "20px",
          }}
        >
          <Grid
            item
            container
            direction="row"
            alignItems={onSetAlignment(fieldHasError("subject"))}
            xs={12}
            md={6}
            spacing={1}
          >
            <Grid
              item
              xs={12}
              md={2}
              style={{
                marginRight: "10px",
              }}
            >
              <Label>
                {translate("subject")}
                <Required>*</Required>
              </Label>
            </Grid>
            <Grid item xs={12} md={8}>
              <Input
                name="subject"
                value={
                  selectedEmailTemplate ? selectedEmailTemplate?.subject : sendEmailTemplate.subject
                }
                onChange={(event) => {
                  if (selectedEmailTemplate) {
                    setSelectedEmailTemplate((state) => ({
                      ...(state || {}),
                      subject: event.target.value,
                    }));
                  } else {
                    setSendEmailTemplate((state) => ({
                      ...(state || {}),
                      subject: event.target.value,
                    }));
                  }
                }}
                error={fieldHasError("subject")}
                errorMessage={getErrorByField("subject")}
              />
            </Grid>
          </Grid>
        </Grid>

        <TextEditorGridContainer container direction="row" alignItems="flex-start" spacing={2}>
          <GridContainer item xs={12} lg={12}>
            <GridContainerTitle>{translate("emailContent")}</GridContainerTitle>
            <FormError isError={!!getErrorByField("html")}>{getErrorByField("html")}</FormError>
          </GridContainer>
        </TextEditorGridContainer>

        <TextEditorContainer item xs={12} lg={12} spacing={2}>
          <SkeletonComponent isLoading={isEmailTemplatesLoading || isProcessEmailTemplateLoading}>
            <JoditEditor
              ref={textEditorRef}
              value={selectedEmailTemplate ? selectedEmailTemplate.html : sendEmailTemplate.html}
              config={config}
              onBlur={(newContent) => {
                if (selectedEmailTemplate) {
                  setSelectedEmailTemplate((state) => ({ ...(state || {}), html: newContent }));
                } else {
                  setSendEmailTemplate((state) => ({ ...(state || {}), html: newContent }));
                }
              }}
            />
          </SkeletonComponent>
        </TextEditorContainer>
      </ModalBody>
    </Modal>
  );
};

export default forwardRef(SendEmailModal);
